import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const LightboxContext = createContext(null);

export const useLightbox = () => useContext(LightboxContext);

export const LightboxProvider = ({ children }) => {
  const [image, setImage] = useState(null);
  const open = useCallback((src, alt) => setImage({ src, alt }), []);
  const close = useCallback(() => setImage(null), []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);

  return (
    <LightboxContext.Provider value={{ open }}>
      {children}
      <AnimatePresence>
        {image && (
          <motion.div
            data-testid="lightbox-overlay"
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-12 bg-bg/92 backdrop-blur-xl cursor-zoom-out"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={close}
          >
            <div className="noise-overlay pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-overlay" />
            <button
              data-testid="lightbox-close"
              onClick={close}
              aria-label="Close"
              className="absolute top-6 right-6 z-10 w-12 h-12 rounded-full border border-line/70 bg-surface/70 flex items-center justify-center text-textMain hover:text-primary hover:border-primary transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <motion.img
              data-testid="lightbox-image"
              src={image.src}
              alt={image.alt || "The Old Pub"}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[86vh] max-w-[92vw] w-auto h-auto object-contain rounded-sm border border-line/50 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.8)] cursor-default"
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            />
            <span className="absolute bottom-8 left-0 right-0 text-center font-mono text-[10px] uppercase tracking-ultra text-textMuted pointer-events-none">
              Click anywhere or press Esc to close
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </LightboxContext.Provider>
  );
};
